export function buildUrl(path?: string): string {
  if (path?.startsWith("http")) {
    return path;
  }

  let finalPath = path;

  if (finalPath?.startsWith("/")) {
    finalPath = finalPath.slice(1);
  }

  const base = process.env.NEXT_PUBLIC_SITE_URL || "";
  if (!finalPath) {
    return base;
  }

  return `${base}/${finalPath}`;
}

/** Absolute origin for links in outbound email (signing, portal, etc.). */
export function getPublicAppUrl(): string {
  const fromApp = process.env.NEXT_PUBLIC_APP_URL?.trim();
  if (fromApp) return fromApp.replace(/\/$/, "");
  const fromSite = process.env.NEXT_PUBLIC_SITE_URL?.trim();
  if (fromSite) return fromSite.replace(/\/$/, "");
  return "https://venturu.com";
}

export function getPublicAppUrlFromHeaders(
  headers?: Pick<Headers, "get">,
): string {
  if (!headers) return getPublicAppUrl();

  const configuredHosts = new Set<string>();
  for (const configuredUrl of [
    process.env.NEXT_PUBLIC_APP_URL,
    process.env.NEXT_PUBLIC_SITE_URL,
  ]) {
    if (!configuredUrl) continue;
    try {
      configuredHosts.add(new URL(configuredUrl).hostname);
    } catch {
      // Ignore malformed configuration and continue with the safe allowlist.
    }
  }

  const forwardedHost = headers.get("x-forwarded-host")?.split(",")[0]?.trim();
  const forwardedProto =
    headers.get("x-forwarded-proto")?.split(",")[0]?.trim() || "https";
  const candidates = [
    headers.get("origin"),
    forwardedHost ? `${forwardedProto}://${forwardedHost}` : null,
    headers.get("host") ? `https://${headers.get("host")}` : null,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    try {
      const url = new URL(candidate);
      const isVenturuHost =
        url.hostname === "venturu.com" ||
        url.hostname.endsWith(".venturu.com");
      const isLocalHost =
        url.hostname === "localhost" ||
        url.hostname === "127.0.0.1" ||
        url.hostname === "[::1]";
      if (isVenturuHost || isLocalHost || configuredHosts.has(url.hostname)) {
        return `${url.protocol}//${url.host}`;
      }
    } catch {
      // Ignore malformed proxy headers and use configured application URL.
    }
  }

  return getPublicAppUrl();
}

export function buildPublicCDNUrl(key: string): string {
  return `${process.env.NEXT_PUBLIC_CDN_BASE_URL}/${key}`;
}

/** True when the value is an absolute URL on the configured public CDN. */
export function isVenturuCdnUrl(value: string): boolean {
  const base = process.env.NEXT_PUBLIC_CDN_BASE_URL?.trim().replace(/\/$/, "");
  if (!base) return false;

  let url: URL;
  let baseUrl: URL;
  try {
    url = new URL(value);
    baseUrl = new URL(base);
  } catch {
    return false;
  }

  if (url.origin !== baseUrl.origin) return false;

  const basePath = baseUrl.pathname.replace(/\/$/, "");
  if (basePath && basePath !== "/") {
    return url.pathname === basePath || url.pathname.startsWith(`${basePath}/`);
  }

  return (
    url.pathname.startsWith("/usercontent/") ||
    url.pathname.startsWith("/assets/")
  );
}

/** Extract a public-bucket object key from a CDN or path-style storage URL. */
export function storageKeyFromVenturuMediaUrl(
  url: string,
): string | null {
  const marker = "usercontent/";
  const index = url.indexOf(marker);
  if (index >= 0) return url.slice(index);
  if (url.startsWith("assets/") || url.startsWith("usercontent/")) {
    return url;
  }
  return null;
}

/**
 * Map a storage key or any known Venturu media URL to the public CDN origin.
 * Rewrites legacy R2/S3 API URLs (presigned upload targets saved without query).
 */
export function coerceVenturuPublicMediaUrl(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  if (!/^https?:\/\//i.test(value)) {
    return buildPublicCDNUrl(value.replace(/^\//, ""));
  }
  if (isVenturuCdnUrl(value)) return value;
  const key = storageKeyFromVenturuMediaUrl(value);
  if (key) return buildPublicCDNUrl(key);
  return value;
}

/** Accept a storage key or an already-absolute URL. */
export function publicMediaUrl(value: string): string {
  return coerceVenturuPublicMediaUrl(value) ?? value;
}

/**
 * Uploaded images must live on our CDN. An unchanged stored value is kept so
 * older records can still be saved without re-uploading.
 */
export function isHostedImageUrl(
  value: string,
  previous?: string | null,
): boolean {
  if (previous && value === previous) return true;
  return isVenturuCdnUrl(value);
}

export function buildPublicCDNAssetUrl(key: string): string {
  return buildPublicCDNUrl(`assets/${key}`);
}

export function buildStateImageCDNAssetUrl(stateSlug: string): string {
  return buildPublicCDNAssetUrl(`images/locations/us/${stateSlug}.jpg`);
}

export function buildBusinessTypeIconCDNAssetKey(iconName: string): string {
  return `assets/icons/business-types/${iconName}.svg`;
}

export function buildBusinessTypeIconCDNAssetUrl(iconName: string): string {
  return buildPublicCDNUrl(buildBusinessTypeIconCDNAssetKey(iconName));
}

export function buildContentHubFileUrl(
  filename?: string | null,
): string | null {
  if (!filename) {
    return null;
  }

  if (filename.startsWith("http")) {
    return filename;
  }

  const finalFilename = filename.startsWith("/api/media/file/")
    ? filename.slice("/api/media/file/".length)
    : filename;

  return buildPublicCDNUrl(`contenthub/${finalFilename}`);
}

export function buildBusinessTypeImageCDNAssetKey(
  businessTypeSlug: string,
): string {
  return `assets/images/business-types/${businessTypeSlug}.jpg`;
}

export function buildBusinessTypeImageCDNAssetUrl(
  businessTypeSlug: string,
): string {
  return buildPublicCDNUrl(buildBusinessTypeImageCDNAssetKey(businessTypeSlug));
}

export function buildExternalSaleImageCDNAssetKey(id: string): string {
  return `assets/images/external-sales/${id}.jpg`;
}

export function buildExternalSaleImageCDNAssetUrl(id: string): string {
  return buildPublicCDNUrl(buildExternalSaleImageCDNAssetKey(id));
}

export function buildListingUrl<T extends { slug?: string | null }>(
  listing: T,
): string {
  return `/business/${listing.slug}`;
}

export function buildListingGalleryUrl<T extends { slug?: string | null }>(
  listing: T,
): string {
  return `/business/${listing.slug}/gallery`;
}

export function buildAgentProfileUrlFromSlug(slug: string): string {
  return `/u/${slug}`;
}

export function buildAgentProfileUrl(agent: {
  agentSlug?: string | null;
}): string | null {
  if (!agent.agentSlug) return null;
  return buildAgentProfileUrlFromSlug(agent.agentSlug);
}

export function buildChatUrl<T extends { id: number }>(
  conversation: T,
): string {
  return `/chat/${conversation.id}`;
}
